import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentLoadingData,
  type EnrollmentLoadingSubmitInput,
  type EnrollmentLoadingSubmitOutput,
} from '../../../models'
import { createMutationHook, createQueryHook } from '../../../hooks'

/**
 * Get Enrollment Loading data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentLoadingData>
 * @example const response = await getEnrollmentLoadingData()
 */
export const getEnrollmentLoadingData = async (context?: DefaultContext): Promise<EnrollmentLoadingData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentLoadingData',
      query: `mutation getEnrollmentLoadingData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentLoadingData }
  return response.submitEnrollment
}

export const useGetEnrollmentLoadingData = createQueryHook<undefined, EnrollmentLoadingData>(getEnrollmentLoadingData)

/**
 * Submit Enrollment Loading data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentLoadingSubmitOutput>
 * @example const response = await submitEnrollmentLoadingData();
 */
export const submitEnrollmentLoadingData = async (
  enrollmentLoadingSubmitInput: EnrollmentLoadingSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentLoadingSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentLoadingData',
      query: `mutation submitEnrollmentLoadingData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentLoadingSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentLoadingSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentLoadingData = createMutationHook<
  EnrollmentLoadingSubmitInput,
  EnrollmentLoadingSubmitOutput
>(submitEnrollmentLoadingData)
